import { CreateAPIAddressSuccessPayload, FetchAddressesPayload } from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { FetchUserPayload } from '~/actions/user.actions';
import { RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import { AddressType, areAddressesFullyEqual, BaseAddress, isBaseAddress } from '~/types/Address';
import { SavedAPIAddress } from '~/types/APIAddress';
import { OrderSelection } from '~/types/Order';
import { listToMapById } from '~/utils/list';

export type AddressHash = HashMap<string, Maybe<number | BaseAddress>>;

export type APIAddressHash = HashMap<number, SavedAPIAddress>;

type SelectAddressAction = { type: 'select'; key: AddressType; orderSelection?: OrderSelection };

type EditAddressAction = {
  type: 'edit';
  key: AddressType | 'edit' | 'create';
  orderSelection?: OrderSelection;
  isMultipleAddresses?: boolean;
};

type CreateAddressAction = {
  type: 'create';
  key?: AddressType | 'edit' | 'create';
  orderSelection?: OrderSelection;
};

export type AddressBookAction = CreateAddressAction | EditAddressAction | SelectAddressAction;

type AddressBookInstance = {
  readonly selectedAddresses: AddressHash;
  readonly currentAction: Maybe<AddressBookAction>;
  readonly initialized: boolean;
  readonly combineSenderReturnAddresses: boolean;
};

export type AddressBookInstances = HashMap<string, AddressBookInstance>;

export type AddressState = {
  readonly apiAddresses: APIAddressHash;
  // TODO: How to avoid death by a thousand booleans?
  readonly isLoadingApiAddresses: boolean;
  readonly isCreatingApiAddress: boolean; // TODO: Maybe rely on form isSubmitting?
  readonly isUpdatingApiAddress: boolean; // TODO: Maybe rely on form isSubmitting?
  readonly isDeletingApiAddress: boolean;
  readonly errorMessage: string;
  // AddressBook state
  readonly addressBookInstances: AddressBookInstances;
};

const addressBookInitialState = {
  selectedAddresses: {},
  currentAction: null,
  initialized: false,
  combineSenderReturnAddresses: false,
};

export const initialState: AddressState = {
  apiAddresses: {},
  isLoadingApiAddresses: false,
  isCreatingApiAddress: false,
  isUpdatingApiAddress: false,
  isDeletingApiAddress: false,
  errorMessage: '',
  // AddressBook state
  addressBookInstances: {},
};

function clearAddressBookCurrentAction(
  addressBookInstances: AddressBookInstances,
): AddressBookInstances {
  return Object.keys(addressBookInstances).reduce(
    (instances, bookKey) => ({
      ...instances,
      [bookKey]: { ...addressBookInstances[bookKey], currentAction: null },
    }),
    {},
  );
}

function addressReducer(state: AddressState = initialState, action: ActionTypes): AddressState {
  switch (action.type) {
    case ActionKeys.ADDRESSES_FETCH:
      return {
        ...state,
        isLoadingApiAddresses: true,
      };
    case ActionKeys.ADDRESSES_FETCH_SUCCESS:
      const { addresses: addressesFromPayload } = action.payload as FetchAddressesPayload;
      return {
        ...state,
        apiAddresses: <APIAddressHash>listToMapById(addressesFromPayload),
        isLoadingApiAddresses: false,
      };
    case ActionKeys.ADDRESSES_FETCH_ERROR:
      return {
        ...state,
        isLoadingApiAddresses: false,
        errorMessage: action.payload.error,
      };
    case ActionKeys.APIADDRESS_CREATE:
      return {
        ...state,
        isCreatingApiAddress: true,
      };
    case ActionKeys.APIADDRESS_CREATE_SUCCESS: {
      const { address: newAddress } = action.payload as CreateAPIAddressSuccessPayload;
      const { isDefaultSender, isDefaultReturn } = newAddress;

      if (!isDefaultSender && !isDefaultReturn) {
        return {
          ...state,
          isCreatingApiAddress: false,
          apiAddresses: { ...state.apiAddresses, [newAddress.id]: newAddress },
          addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
        };
      }

      const newApiAddresses = Object.values(state.apiAddresses).reduce(
        (newValues, address) => ({
          ...newValues,
          [address.id]: {
            ...address,
            isDefaultSender: isDefaultSender ? false : address.isDefaultSender,
            isDefaultReturn: isDefaultReturn ? false : address.isDefaultReturn,
          },
        }),
        {},
      );

      return {
        ...state,
        isCreatingApiAddress: false,
        apiAddresses: { ...newApiAddresses, [newAddress.id]: newAddress },
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
      };
    }
    case ActionKeys.APIADDRESS_CREATE_ERROR:
      return {
        ...state,
        isCreatingApiAddress: false,
        errorMessage: action.payload.error,
      };
    case ActionKeys.APIADDRESS_EDIT:
      return {
        ...state,
        isUpdatingApiAddress: true,
        errorMessage: '',
      };
    case ActionKeys.APIADDRESS_EDIT_SUCCESS: {
      const updatedAddress = action.payload.address;
      const { isDefaultSender, isDefaultReturn } = updatedAddress;

      const apiAddresses = Object.values(state.apiAddresses).reduce(
        (newAddresses, address) =>
          address.id === updatedAddress.id
            ? {
                ...newAddresses,
                [updatedAddress.id]: updatedAddress,
              }
            : {
                ...newAddresses,
                [address.id]: {
                  ...address,
                  isDefaultSender: isDefaultSender ? false : address.isDefaultSender,
                  isDefaultReturn: isDefaultReturn ? false : address.isDefaultReturn,
                },
              },
        {},
      );

      return {
        ...state,
        apiAddresses,
        isUpdatingApiAddress: false,
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
      };
    }
    case ActionKeys.APIADDRESS_EDIT_ERROR:
      return {
        ...state,
        isUpdatingApiAddress: false,
        errorMessage: action.payload.error,
      };
    case ActionKeys.APIADDRESS_DELETE:
      return {
        ...state,
        isDeletingApiAddress: true,
        errorMessage: '',
      };
    case ActionKeys.APIADDRESS_DELETE_ERROR:
      return {
        ...state,
        isDeletingApiAddress: false,
        errorMessage: action.payload.error,
      };
    case ActionKeys.APIADDRESS_DELETE_SUCCESS:
      // TODO: What should be the selected address after delete?
      // We cannot rely on defaulting via the key if return or sender because that would
      // be hardcoding the shit out of this and that is not needed for other functionality
      // like addresses settings

      return {
        ...state,
        apiAddresses: omit(state.apiAddresses, [action.payload.id]),
        isDeletingApiAddress: false,
        addressBookInstances: clearAddressBookCurrentAction(state.addressBookInstances),
      };
    case ActionKeys.ADDRESS_BOOK_ACTION:
      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...state.addressBookInstances[action.payload.addressBookName],
            currentAction: action.payload.action,
          },
        },
      };
    case ActionKeys.ADDRESS_BOOK_INITIALIZE:
      if (
        state.addressBookInstances[action.payload.addressBookName] &&
        state.addressBookInstances[action.payload.addressBookName].initialized
      ) {
        return state;
      }
      const addresses = action.payload.addresses;
      const addressesKeys = Object.keys(addresses);
      const senderAddress = addresses[SENDER_ADDRESS_KIND];
      const returnAddress = addresses[RETURN_ADDRESS_KIND];

      // If both `senderAddress` and `returnAddress` are <BaseAddress>, compare them as such
      const areAddressesEqual =
        senderAddress &&
        isBaseAddress(senderAddress) &&
        returnAddress &&
        isBaseAddress(returnAddress) &&
        areAddressesFullyEqual(senderAddress, returnAddress);

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBookInitialState,
            selectedAddresses: action.payload.addresses,
            combineSenderReturnAddresses:
              action.payload.combineSenderReturnAddresses !== undefined
                ? action.payload.combineSenderReturnAddresses
                : addressesKeys.length > 0 && // Check for settings/address that initializes with no address
                  (areAddressesEqual || isEqual(senderAddress, returnAddress)),
            initialized: true,
          },
        },
      };
    case ActionKeys.ADDRESS_BOOK_SELECT: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      const selectedAddresses = addressBook.selectedAddresses;

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            currentAction: null,
            selectedAddresses:
              addressBook.combineSenderReturnAddresses && action.payload.key === SENDER_ADDRESS_KIND
                ? {
                    ...selectedAddresses,
                    [action.payload.key]: action.payload.address,
                    [RETURN_ADDRESS_KIND]: action.payload.address,
                  }
                : {
                    ...selectedAddresses,
                    [action.payload.key]: action.payload.address,
                  },
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_CLEAR_SELECTION: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      const selectedAddresses = addressBook.selectedAddresses;

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            currentAction: null,
            selectedAddresses: {
              ...selectedAddresses,
              [action.payload.key]: null,
            },
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_SELECT_DEFAULT:
      const { key, addressBookName } = action.payload;

      if (
        !Object.keys(state.addressBookInstances[addressBookName].selectedAddresses).includes(key)
      ) {
        return state;
      }

      const apiAddresses = Object.values(state.apiAddresses);
      const defaultSenderAddress = apiAddresses.find((x: SavedAPIAddress) => !!x.isDefaultSender);
      const defaultReturnAddress = apiAddresses.find((x: SavedAPIAddress) => !!x.isDefaultReturn);

      const defaults: Record<string, typeof defaultSenderAddress> = {
        sender: defaultSenderAddress,
        return: defaultReturnAddress,
      };

      if (
        state.addressBookInstances[addressBookName].selectedAddresses[SENDER_ADDRESS_KIND] ===
        state.addressBookInstances[addressBookName].selectedAddresses[RETURN_ADDRESS_KIND]
      ) {
        return {
          ...state,
          addressBookInstances: {
            ...state.addressBookInstances,
            [addressBookName]: {
              ...state.addressBookInstances[addressBookName],
              selectedAddresses: {
                ...state.addressBookInstances[addressBookName].selectedAddresses,
                sender: defaults.sender ? defaults.sender.id! : null,
                return: defaults.return ? defaults.return.id! : null,
              },
            },
          },
        };
      }

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...state.addressBookInstances[action.payload.addressBookName],
            selectedAddresses: {
              ...state.addressBookInstances[action.payload.addressBookName].selectedAddresses,
              [key]: defaults[key] ? defaults[key]?.id : null,
            },
          },
        },
      };

    case ActionKeys.ADDRESS_BOOK_TOGGLE_COMBINE_SENDER_RETURN_ADDRESSES: {
      const addressBook = state.addressBookInstances[action.payload.addressBookName];
      const { selectedAddresses } = addressBook;
      const updatedCombine = !addressBook.combineSenderReturnAddresses;

      return {
        ...state,
        addressBookInstances: {
          ...state.addressBookInstances,
          [action.payload.addressBookName]: {
            ...addressBook,
            combineSenderReturnAddresses: updatedCombine,
            selectedAddresses: updatedCombine
              ? {
                  ...selectedAddresses,
                  [RETURN_ADDRESS_KIND]: selectedAddresses[SENDER_ADDRESS_KIND],
                }
              : selectedAddresses,
          },
        },
      };
    }
    case ActionKeys.ADDRESS_BOOK_CLEAR_STATE:
      return {
        ...state,
        addressBookInstances: {
          ...Object.keys(state.addressBookInstances).reduce((instances, key) => {
            if (key === action.payload.addressBookName) return instances;

            return { ...instances, [key]: state.addressBookInstances[key] };
          }, {}),
        },
      };
    case ActionKeys.USER_FETCH_SUCCESS:
      const userFetchPayload: FetchUserPayload = action.payload;
      const defaultSenderAddressId: Maybe<number> = userFetchPayload.user.address
        ? userFetchPayload.user.address.id
          ? userFetchPayload.user.address.id
          : null
        : null;
      const defaultReturnAddressId: Maybe<number> = userFetchPayload.user.addressReturn
        ? userFetchPayload.user.addressReturn.id
          ? userFetchPayload.user.addressReturn.id
          : null
        : null;

      const updatedAddresses = Object.values(state.apiAddresses).reduce((updated, apiAddress) => {
        if (defaultSenderAddressId && apiAddress.id === defaultSenderAddressId) {
          return { ...updated, [defaultSenderAddressId]: userFetchPayload.user.address };
        }

        if (defaultReturnAddressId && apiAddress.id === defaultReturnAddressId) {
          return { ...updated, [defaultReturnAddressId]: userFetchPayload.user.addressReturn };
        }

        return { ...updated, [apiAddress.id]: apiAddress };
      }, {});

      return {
        ...state,
        apiAddresses: updatedAddresses,
      };

    default:
      return state;
  }
}

export default addressReducer;
